import { ModalWithState } from 'alurkerja-ui'
import { useUiFromSpecContext } from '../../EditorContextProvider.js'
import { ReactNode, useState } from 'react'
import EditActions from './EditActions.js'
import GeneralEditElement from './Panels/GeneralEditElement.js'
import LogicEditElement from './Panels/LogicEditElement.js'
import { Tab } from '@headlessui/react'
import clsx from 'clsx'
import ValidationEditElement from './Panels/ValidationEditElement.js'
import { UseFormReturn } from 'react-hook-form'
import OtherEditElement from './Panels/OtherEditElement.js'
import { stringToTitleCase } from '@/utils/functions/stringToTitleCase'

type EditElementProps = {
  form: UseFormReturn<any>
}
type TabsType = {
  label: string

  content: React.ReactNode
}

export type CustomEditElementProps = {
  element: string
  index: number
  currentElementState: any
  setCurrentElementState: React.Dispatch<any>
  defaultEditElementProps: ReactNode
}
export type CustomEditElementType = (props: CustomEditElementProps) => ReactNode

export const EditElement = ({ form }: EditElementProps) => {
  const { setShow, selectedElement, setSelectedPath } = useUiFromSpecContext()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [currentElementState, setCurrentElementState] = useState({ ...selectedElement })

  const hasNameField = 'name' in currentElementState
  const editTabs = currentElementState?.edit_tabs ?? {}
  const tabs: TabsType[] = [
    {
      label: 'General',
      content: (
        <GeneralEditElement
          form={form}
          currentElementState={currentElementState}
          setCurrentElementState={setCurrentElementState}
        />
      ),
    },
    {
      label: 'Logic',
      content: (
        <LogicEditElement currentElementState={currentElementState} setCurrentElementState={setCurrentElementState} />
      ),
    },
    // Only show Validation tab if currentElementState doesn't have a name key
    ...(hasNameField
      ? [
          {
            label: 'Validation',
            content: (
              <ValidationEditElement
                form={form}
                currentElementState={currentElementState}
                setCurrentElementState={setCurrentElementState}
              />
            ),
          },
        ]
      : []),
    ...(Object.keys(editTabs).length > 0
      ? Object.entries(editTabs).map(([key]) => ({
          label: stringToTitleCase(key),
          content: (
            <OtherEditElement
              form={form}
              keyTab={key}
              currentElementState={currentElementState}
              setCurrentElementState={setCurrentElementState}
            />
          )
        }))
      : []),
  ]

  return (
    <ModalWithState
      title="Edit Element"
      setShow={() => {
        setShow && setShow(false)
        setSelectedPath && setSelectedPath('')
      }}
      onClose={() => {
        setSelectedPath && setSelectedPath('')
      }}
      contentClassName="!px-0"
    >
      <div className="overflow-y-auto py-2 h-[calc(100vh-300px)] px-2.5">
        <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
          <Tab.List className="flex space-x-1 w-full rounded-lg bg-slate-100 sticky top-0 p-1.5 z-10">
            {tabs.map((item) => (
              <Tab
                key={item.label}
                className={({ selected }) =>
                  clsx(
                    'rounded-lg py-2 px-3 w-full text-xs font-medium leading-5',
                    'focus:ring-0 focus:outline-none',
                    'transition-all duration-300 ease-in-out',
                    'hover:scale-105 transform',
                    selected ? 'bg-white text-black shadow scale-105' : 'text-black hover:text-slate-700'
                  )
                }
              >
                <div className="flex items-center justify-center gap-1 transition-all duration-200">
                  <span>{item.label}</span>
                </div>
              </Tab>
            ))}
          </Tab.List>
          <Tab.Panels className="mt-4">
            {tabs.map(({ label, content }) => (
              <Tab.Panel key={label} className="transition-al duration-300 ease-in-out">
                <div className="animate-fade-in">{content}</div>
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </div>

      <EditActions
        form={form}
        currentElementState={currentElementState}
        setCurrentElementState={setCurrentElementState}
      />
    </ModalWithState>
  )
}
